/* ==========================================================================
   That Party Place — custom behaviour
   Small, dependency-free, transform/opacity only. Everything degrades to a
   working page if this file never loads.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animOff = root.classList.contains('anim-off');
  var revealOff = root.classList.contains('reveal-off') || animOff || reduced;

  /* ---------------------------------------------------------- reveal ---- */

  function reveal() {
    var els = document.querySelectorAll('.rv:not(.is-in)');
    if (!els.length) return;

    if (revealOff || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* --------------------------------------------------------- count up ---- */

  function countUp() {
    var els = document.querySelectorAll('[data-count-to]:not([data-count-bound])');
    if (!els.length) return;

    els.forEach(function (el) {
      var target = parseInt(el.getAttribute('data-count-to'), 10);
      if (isNaN(target)) return;

      // Claim the element straight away. This function also runs on
      // shopify:section:load, and without claiming it here a second run could
      // attach a second animation, read the half-counted number as its "final"
      // value, and leave the countdown showing 7 instead of 77.
      el.setAttribute('data-count-bound', '');

      // Derived from the attribute, never from live text, for the same reason.
      var final = target.toLocaleString('en-GB');

      // The correct number is already rendered server side, so it is right
      // without JS, right for search engines, and right with animation off.
      if (animOff || reduced || !('IntersectionObserver' in window)) {
        el.textContent = final;
        return;
      }

      var o = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || el.hasAttribute('data-counted')) return;
            el.setAttribute('data-counted', '');
            o.disconnect();

            var start = performance.now();
            var dur = 900 * (parseFloat(getComputedStyle(root).getPropertyValue('--anim-scale')) || 1);

            (function step(now) {
              var p = Math.min((now - start) / dur, 1);
              var v = Math.round(target * (1 - Math.pow(1 - p, 3)));
              el.textContent = v.toLocaleString('en-GB');
              if (p < 1) requestAnimationFrame(step);
              else el.textContent = final;
            })(start);
          });
        },
        { threshold: 0.5 }
      );
      o.observe(el);
    });
  }

  /* ------------------------------------------------------ cart bubble ---- */

  function pulseCart() {
    if (animOff || reduced) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.classList.remove('is-pulsing');
      // Force a reflow so the animation restarts on a repeat add.
      void el.offsetWidth;
      el.classList.add('is-pulsing');
    });
  }

  // Dawn publishes a cartUpdate event through assets/pubsub.js.
  document.addEventListener('tpp:cart:updated', pulseCart);
  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, pulseCart);
  }

  /* ------------------------------------------------------- quick add ----- */

  function refreshCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
  }

  // Progressive enhancement: without JS the form posts normally and Shopify
  // redirects to the cart, which is a perfectly good outcome.
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-quick-add]');
    if (!form) return;

    e.preventDefault();

    var button = form.querySelector('button');
    var original = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = window.tppStrings ? window.tppStrings.adding : '…';
    }

    fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: form.querySelector('[name="id"]').value, quantity: 1 }],
      }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'error');
          return data;
        });
      })
      .then(function () {
        return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          });
      })
      .then(function (cart) {
        refreshCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('tpp:cart:updated', { detail: { cart: cart } }));
        openCartDrawer();
        if (button) {
          button.textContent = window.tppStrings ? window.tppStrings.added : '✓';
          setTimeout(function () {
            button.textContent = original;
            button.disabled = false;
          }, 1600);
        }
      })
      .catch(function () {
        // Fall back to the real form post so the shopper is never stuck.
        if (button) {
          button.textContent = original;
          button.disabled = false;
        }
        form.submit();
      });
  });

  /* ----------------------------------------------------- cart drawer ----- */

  // Contents come back from the section itself, so prices and wording stay in
  // Liquid rather than being rebuilt here.
  function refreshCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return Promise.resolve();

    return fetch(window.routes.cart_url + '?section_id=cart-drawer')
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer]');
        if (!fresh) return;

        var wasOpen = drawer.hasAttribute('open');
        drawer.innerHTML = fresh.innerHTML;
        if (wasOpen) drawer.setAttribute('open', '');
      })
      .catch(function () {
        /* Leave the drawer as it is; the basket page is still correct. */
      });
  }

  function openCartDrawer(opener) {
    var el = document.querySelector('[data-cart-drawer]');
    // With the drawer switched off, adding simply leaves the count updated.
    if (!el) return;

    refreshCartDrawer().then(function () {
      // Opened through the shared drawer controller, not by setting the
      // attribute here. The close button, the overlay and Escape all ask that
      // controller which panel is open — opening the basket behind its back
      // left it holding nothing, so there was no way to shut the drawer again.
      tppDrawer.open(el.id, opener);
    });
  }

  // Quantity steppers and remove inside the drawer.
  document.addEventListener('click', function (e) {
    var control = e.target.closest('[data-line-change]');
    if (!control) return;
    e.preventDefault();

    var line = control.getAttribute('data-line-change');
    var qty = control.getAttribute('data-line-qty');

    fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        refreshCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('tpp:cart:updated', { detail: { cart: cart } }));
        return refreshCartDrawer();
      })
      .catch(function () {
        window.location = window.routes.cart_url;
      });
  });

  // Typing a quantity straight into the box.
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-line-input]');
    if (!input) return;
    var line = input.getAttribute('data-line-input');
    var qty = Math.max(0, parseInt(input.value, 10) || 0);

    fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        refreshCartCount(cart.item_count);
        return refreshCartDrawer();
      })
      .catch(function () {
        window.location = window.routes.cart_url;
      });
  });

  // The basket button in the header opens the drawer instead of navigating,
  // but only when the drawer exists.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('#cart-icon-bubble');
    if (!link) return;
    if (!document.querySelector('[data-cart-drawer]')) return;
    e.preventDefault();
    openCartDrawer(link);
  });

  /* --------------------------------------------------------- drawer ------ */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  var TppDrawer = function () {
    var self = this;
    this.el = null;
    this.opener = null;

    this.open = function (id, opener) {
      var el = document.getElementById(id);
      if (!el) return;
      self.el = el;
      self.opener = opener || null;
      el.setAttribute('open', '');
      root.classList.add('drawer-open');
      if (opener) opener.setAttribute('aria-expanded', 'true');

      // Move focus in on the next frame so the transition has started.
      requestAnimationFrame(function () {
        var first = el.querySelector('[data-drawer-close]') || el.querySelector(FOCUSABLE);
        if (first) first.focus();
      });
    };

    this.close = function () {
      if (!self.el) return;
      self.el.removeAttribute('open');
      root.classList.remove('drawer-open');
      if (self.opener) {
        self.opener.setAttribute('aria-expanded', 'false');
        self.opener.focus();
      }
      self.el = null;
      self.opener = null;
    };

    this.trap = function (e) {
      if (!self.el || e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(self.el.querySelectorAll(FOCUSABLE), function (n) {
        return n.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
  };

  var tppDrawer = new TppDrawer();

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      tppDrawer.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.hasAttribute('data-drawer-overlay')) {
      e.preventDefault();
      tppDrawer.close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') tppDrawer.close();
    tppDrawer.trap(e);
  });

  /* ------------------------------------------- announcement rotation ----- */

  function rotateAnnouncements() {
    document.querySelectorAll('[data-ann-rotate]').forEach(function (group) {
      if (group.dataset.annBound) return;
      group.dataset.annBound = '1';

      var items = group.querySelectorAll('.ann__item');
      if (items.length < 2) return;

      // Auto-updating content is a barrier when motion is reduced, so the
      // first message simply stays put instead.
      if (animOff || reduced) return;

      var delay = (parseFloat(group.getAttribute('data-ann-rotate')) || 6) * 1000;
      var i = 0;
      var timer = null;

      function step() {
        items[i].hidden = true;
        i = (i + 1) % items.length;
        items[i].hidden = false;
      }

      function start() {
        if (!timer) timer = setInterval(step, delay);
      }

      function stop() {
        clearInterval(timer);
        timer = null;
      }

      // WCAG 2.2.2 — the reader must be able to hold it still.
      group.addEventListener('mouseenter', stop);
      group.addEventListener('mouseleave', start);
      group.addEventListener('focusin', stop);
      group.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      start();
    });
  }

  /* ------------------------------------------------- sticky header shadow -- */

  function stickyHeader() {
    var header = document.querySelector('[data-sticky-header]');
    if (!header) return;
    var ticking = false;

    function update() {
      header.classList.toggle('is-stuck', window.scrollY > 8);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  /* ------------------------------------------ same-day dispatch clock ---- */

  // The deadline arrives from Liquid stamped with the shop's UTC offset, so
  // this counts to the shop's cut-off wherever the shopper happens to be.
  function dispatchClock() {
    var els = document.querySelectorAll('[data-dispatch-countdown]');

    Array.prototype.forEach.call(els, function (el) {
      // Claimed straight away: this also runs on shopify:section:load, and a
      // second binding would leave two intervals fighting over one element.
      if (el.hasAttribute('data-dispatch-bound')) return;
      el.setAttribute('data-dispatch-bound', '');

      var msg = el.querySelector('[data-dispatch-msg]');
      var deadline = new Date(el.getAttribute('data-deadline')).getTime();
      var before = el.getAttribute('data-before') || '';
      var after = el.getAttribute('data-after') || '';
      var weekday = parseInt(el.getAttribute('data-weekday'), 10);
      var days = el.getAttribute('data-days') || 'weekdays';
      var cutMins = parseInt(el.getAttribute('data-cut-mins'), 10) || 0;

      // An unreadable date would otherwise count down from 1970 and show the
      // closed wording for ever. Leave whatever Liquid rendered instead.
      if (!msg || isNaN(deadline) || isNaN(weekday)) return;

      // Midnight tonight, shop time — derived from the deadline, which already
      // carries the shop's offset, so no timezone guessing happens here.
      var nextMidnight = deadline - cutMins * 60000 + 86400000;

      function dispatches(wd) {
        if (days === 'everyday') return true;
        if (days === 'mon_sat') return wd !== 0;
        return wd !== 0 && wd !== 6;
      }

      var open = dispatches(weekday);

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      // Built from nodes rather than innerHTML so the shop's own wording is
      // never treated as markup.
      function paint(template, timeText) {
        var parts = template.split('[time]');
        msg.textContent = parts[0];
        if (parts.length > 1) {
          var strong = document.createElement('span');
          strong.className = 'dispatch__t';
          strong.textContent = timeText;
          msg.appendChild(strong);
          msg.appendChild(document.createTextNode(parts.slice(1).join('[time]')));
        }
      }

      function tick() {
        var now = Date.now();

        // Once midnight passes the shop has a fresh cut-off, so the deadline
        // rolls on instead of the message sticking at "you have missed it".
        // Without this, a tab left open overnight would still be turning a
        // shopper away at nine the next morning.
        //
        // A day is taken as 24 hours. On the two nights a year the clocks
        // change, a tab left open across both midnight and the change would be
        // an hour out; a reload fixes it, and that is not worth more machinery.
        while (now >= nextMidnight) {
          deadline += 86400000;
          nextMidnight += 86400000;
          weekday = (weekday + 1) % 7;
          open = dispatches(weekday);
        }

        var left = deadline - now;

        if (!open || left <= 0) {
          paint(after, '');
          el.setAttribute('data-state', 'closed');
          return;
        }

        var total = Math.floor(left / 1000);
        var h = Math.floor(total / 3600);
        var m = Math.floor((total % 3600) / 60);
        var s = total % 60;

        el.setAttribute('data-state', 'open');
        paint(before, h > 0 ? h + 'h ' + pad(m) + 'm ' + pad(s) + 's' : m + 'm ' + pad(s) + 's');
      }

      // Kept ticking even when closed: that is what notices midnight arriving.
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ------------------------------------------------------------- init ---- */

  function init() {
    reveal();
    countUp();
    stickyHeader();
    rotateAnnouncements();
    dispatchClock();
  }

  /* The sort dropdown carries data-facet-auto but nothing ever listened for it,
     so changing the sort order did nothing — the same omission that left the
     Apply button dead. A select cannot submit itself, so this genuinely needs
     script; the filters do not, and no longer depend on any.

     Delegated from the document so it survives the theme editor replacing a
     section, and so it costs nothing on pages with no filters. */
  document.addEventListener('change', function (e) {
    var trigger = e.target.closest('[data-facet-auto]');
    if (!trigger) return;

    var form = trigger.closest('[data-facet-form]');
    if (!form) return;

    // requestSubmit fires the submit event and respects validation the way a
    // real button press does. submit() is the fallback for older Safari.
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      form.submit();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', function () {
    reveal();
    countUp();
    stickyHeader();
    rotateAnnouncements();
    dispatchClock();
  });
})();
